import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { User } from 'oidc-client-ts';
import { filter, take } from 'rxjs/operators';
import { SignalingConnectionService } from '../signaling/signaling-connection.service';
import { FeatureConfigurationService } from '../api/feature-configuration.service';
import { FeatureConfigurationDataService } from '../data/feature-configuration-data.service';
import { PageVisitService } from '../util/page-visit.service';
import { sentryLogger } from 'shared/core';
import { HelperDeferredAppointmentCompletionService } from '../deferred-appointment-completion.service';

@Injectable()
export class AppStartupService {
  private isStartupDeferred = false;
  private deferredUserIdentifier: string | null = null;
  private startedForUserIdentifier: string | null = null;

  constructor(
    private readonly router: Router,
    private readonly signalingConnectionService: SignalingConnectionService,
    private readonly featureConfigurationService: FeatureConfigurationService,
    private readonly featureConfigurationDataService: FeatureConfigurationDataService,
    private readonly pageVisitService: PageVisitService,
    private readonly deferredAppointmentCompletion: HelperDeferredAppointmentCompletionService,
  ) {}

  public startUp(user: User | null): void {
    if (!this.isAuthenticatedUser(user)) {
      this.startedForUserIdentifier = null;
      this.deferredUserIdentifier = null;
      this.isStartupDeferred = false;
      this.deferredAppointmentCompletion.pause();
      return;
    }

    const userIdentifier = user.profile.sub;
    if (this.router.url.startsWith('/auth-callback')) {
      this.deferStartupUntilAfterAuthCallback(userIdentifier);
      return;
    }

    if (this.startedForUserIdentifier === userIdentifier) {
      this.deferredAppointmentCompletion.start();
      return;
    }

    this.runAuthenticatedStartup(userIdentifier);
  }

  public tearDown(): void {
    this.signalingConnectionService.disconnect();
    this.deferredAppointmentCompletion.pause();
  }

  private isAuthenticatedUser(user: User | null): user is User {
    return !!user?.access_token && !user.expired;
  }

  private deferStartupUntilAfterAuthCallback(userIdentifier: string): void {
    this.deferredUserIdentifier = userIdentifier;

    if (this.isStartupDeferred) {
      return;
    }
    this.isStartupDeferred = true;

    // During OIDC callback processing, authentication state is still settling.
    // Run once after navigation leaves auth-callback to avoid transient 401s.
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
        filter(
          (event) => !event.urlAfterRedirects.startsWith('/auth-callback'),
        ),
        take(1),
      )
      .subscribe(() => {
        this.isStartupDeferred = false;
        const deferredUserIdentifier = this.deferredUserIdentifier;
        this.deferredUserIdentifier = null;
        if (!deferredUserIdentifier) {
          return;
        }

        if (this.startedForUserIdentifier === deferredUserIdentifier) {
          this.deferredAppointmentCompletion.start();
        } else {
          this.runAuthenticatedStartup(deferredUserIdentifier);
        }
      });
  }

  private runAuthenticatedStartup(userIdentifier: string): void {
    this.startedForUserIdentifier = userIdentifier;
    this.deferredAppointmentCompletion.start();
    this.signalingConnectionService.connect(userIdentifier);
    this.pageVisitService.reportPageVisitOnce('helperClient');
    this.featureConfigurationService
      .getActiveFeatureConfiguration()
      .pipe(take(1))
      .subscribe({
        next: (config) => {
          this.featureConfigurationDataService.updateFeatureConfiguration(
            config,
          );
        },
        error: (error) => {
          // Startup should continue even if this non-critical request fails.
          sentryLogger.warn(
            'Failed to load helper feature configuration during startup',
            {
              error: error,
              userIdentifier: userIdentifier,
            },
          );
        },
      });
  }
}
